import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge, fmtDate, Field, TextInput, ErrorBox, btnPrimary, inputCls } from "@/components/common/Form";
import { EmptyState, LoadingState, ErrorState } from "@/components/common/States";
import { RfqItems, QuoteCard, RfqMeta } from "@/pages/business/RfqPages";

const wrap = "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10";

export function VendorRfqs() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let c = false;
    api.get("/vendor/rfqs").then((r) => { if (!c) setRows(r.data); }).catch((e) => { if (!c) { setRows([]); setError(formatApiError(e.response?.data?.detail)); } });
    return () => { c = true; };
  }, []);
  if (rows === null) return <LoadingState />;
  return (
    <div className={wrap} data-testid="vendor-rfqs-page">
      <h1 className="text-3xl font-serif font-bold text-stone-900 mb-6">Wholesale RFQs</h1>
      {error && <ErrorBox message={error} />}
      {rows.length === 0 && !error ? <EmptyState title="No quote requests yet" message="Business buyers' RFQs for your wholesale products will appear here." /> : (
        <ul className="space-y-3" data-testid="vendor-rfq-list">
          {rows.map((r) => (
            <li key={r.id}><Link to={`/vendor/rfqs/${r.id}`} data-testid={`vendor-rfq-row-${r.rfq_number}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-4 hover:border-primary/50">
              <div className="h-11 w-11 rounded-full bg-accent grid place-items-center text-secondary"><FileText size={20} /></div>
              <div className="flex-1 min-w-[180px]"><div className="font-semibold text-stone-900">{r.rfq_number}</div><div className="text-xs text-stone-500">{fmtDate(r.created_at)} · {r.business_name || "Business buyer"} · {r.item_count} units · {r.delivery_city}</div></div>
              <StatusBadge value={r.status} />
            </Link></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function VendorRfqDetail() {
  const { id } = useParams();
  const [rfq, setRfq] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ quoted_unit_price: "", quoted_quantity: "", note: "", validity_days: 7 });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get(`/vendor/rfqs/${id}`).then((r) => {
    setRfq(r.data);
    setForm((f) => ({ ...f, quoted_quantity: f.quoted_quantity || r.data.items.reduce((s, i) => s + i.quantity, 0), quoted_unit_price: f.quoted_unit_price || r.data.items[0]?.requested_unit_price || "" }));
  }).catch((e) => setError(formatApiError(e.response?.data?.detail))), [id]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setFormError(""); setSaving(true);
    try {
      const r = await api.post(`/vendor/rfqs/${id}/quote`, { quoted_unit_price: Number(form.quoted_unit_price), quoted_quantity: Number(form.quoted_quantity), note: form.note, validity_days: Number(form.validity_days) });
      setRfq(r.data); toast.success("Quotation sent to buyer");
    } catch (err) { setFormError(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (error) return <ErrorState message={error} />;
  if (!rfq) return <LoadingState />;
  const active = rfq.quotes.find((q) => q.status === "ACTIVE") || rfq.quotes[0];
  const canQuote = ["SUBMITTED", "VIEWED", "QUOTED", "EXPIRED"].includes(rfq.status);
  return (
    <div className={wrap} data-testid="vendor-rfq-detail">
      <Link to="/vendor/rfqs" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-secondary mb-4"><ArrowLeft size={14} /> All RFQs</Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-serif font-bold text-stone-900" data-testid="vendor-rfq-number">{rfq.rfq_number}</h1>
        <StatusBadge value={rfq.status} testId="vendor-rfq-status" />
      </div>
      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="space-y-4">
          <RfqItems rfq={rfq} />
          {active && <QuoteCard quote={active} />}
          {canQuote && (
            <form onSubmit={submit} className="rounded-xl border border-border bg-card p-4 grid sm:grid-cols-2 gap-4" data-testid="quote-form">
              <h3 className="sm:col-span-2 font-semibold text-stone-900">{active ? "Send a revised quotation" : "Send quotation"}</h3>
              <div className="sm:col-span-2"><ErrorBox message={formError} testId="quote-error" /></div>
              <Field label="Quoted unit price (₹) *"><TextInput testId="quote-unit-price" type="number" step="0.01" min="0.01" required value={form.quoted_unit_price} onChange={set("quoted_unit_price")} /></Field>
              <Field label="Quoted quantity *"><TextInput testId="quote-quantity" type="number" min="1" required value={form.quoted_quantity} onChange={set("quoted_quantity")} /></Field>
              <Field label="Valid for (days)"><TextInput testId="quote-validity" type="number" min="1" max="90" value={form.validity_days} onChange={set("validity_days")} /></Field>
              <Field label="Note to buyer" className="sm:col-span-2"><textarea rows={3} data-testid="quote-note" value={form.note} onChange={set("note")} className={inputCls} /></Field>
              <div className="sm:col-span-2"><button disabled={saving} data-testid="quote-submit" className={btnPrimary}>{saving ? "Sending..." : "Send quotation"}</button></div>
            </form>
          )}
        </div>
        <RfqMeta rfq={rfq} vendorView />
      </div>
    </div>
  );
}
